import { Pause, Play, X } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { Avatar } from '@/components/tree/Avatar'
import type { Kitchen } from '@/lib/modernRecipes'
import { photoUrl } from '@/lib/photos'
import { cn } from '@/lib/utils'

const SECONDS = 6

// From the kitchen of…: one person's recipes played one after another, each as its card in their own hand (or the dish,
// when there's no card), newest first. Tap the right side for the next, the left for the one before; hold to pause.
// It stops on the last card rather than looping, and moves on by itself only when the person hasn't asked for less
// motion.
export function KitchenStory({ kitchen, onClose }: { kitchen: Kitchen; onClose: () => void }) {
  const items = kitchen.recipes
  const [at, setAt] = useState(0)
  const reduced = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches
  const [paused, setPaused] = useState(reduced)
  const recipe = items[at]!
  const picture = recipe.card ?? recipe.tile
  // Each card stays for a few seconds, then the next comes; the bar along the top fills as it waits.
  const [progress, setProgress] = useState(0)
  const go = (by: number) => {
    setAt((n) => Math.max(0, Math.min(items.length - 1, n + by)))
    setProgress(0)
  }
  useEffect(() => {
    if (paused) return
    let frame = 0
    const from = performance.now() - progress * SECONDS * 1000
    const tick = (now: number) => {
      const done = (now - from) / (SECONDS * 1000)
      if (done >= 1) {
        if (at < items.length - 1) {
          setAt(at + 1)
          setProgress(0)
        } else setPaused(true)
        return
      }
      setProgress(done)
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
    // progress is read once as the starting point when playing resumes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, at, items.length])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') go(1)
      else if (e.key === 'ArrowLeft') go(-1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  const hold = useRef<number | null>(null)
  return (
    <DialogPrimitive.Root open onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          aria-describedby={undefined}
          data-testid="kitchen-story"
          className="fixed inset-0 z-[60] flex flex-col bg-viewer text-viewer-foreground outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 motion-reduce:data-[state=open]:zoom-in-100"
        >
          <div className="flex flex-col gap-3 px-3 pt-[calc(var(--safe-top)+0.5rem)] sm:px-5">
            <div className="flex gap-1" aria-hidden>
              {items.map((x, i) => (
                <span key={x.id} className="h-[3px] flex-1 overflow-hidden rounded-full bg-viewer-foreground/30">
                  <span
                    className="block h-full bg-viewer-foreground"
                    style={{ width: `${i < at ? 100 : i === at ? progress * 100 : 0}%` }}
                  />
                </span>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <Avatar person={kitchen.person} className="size-9 text-sm shadow-none" />
              <div className="min-w-0 flex-1 leading-tight">
                <DialogPrimitive.Title className="truncate text-sm font-semibold">
                  From the kitchen of {kitchen.person.name}
                </DialogPrimitive.Title>
                <p className="truncate text-xs text-viewer-muted">
                  {recipe.title} · {at + 1} of {items.length}
                </p>
              </div>
              <button
                type="button"
                aria-label={paused ? 'Play' : 'Pause'}
                onClick={() => setPaused((p) => !p)}
                className="grid size-11 place-items-center rounded-full outline-none hover:bg-viewer-foreground/12 focus-visible:ring-[3px] focus-visible:ring-ring/60"
              >
                {paused ? <Play className="size-5" aria-hidden /> : <Pause className="size-5" aria-hidden />}
              </button>
              <DialogPrimitive.Close
                aria-label="Close"
                className="grid size-11 place-items-center rounded-full outline-none hover:bg-viewer-foreground/12 focus-visible:ring-[3px] focus-visible:ring-ring/60"
              >
                <X aria-hidden />
              </DialogPrimitive.Close>
            </div>
          </div>

          <div
            className="relative min-h-0 flex-1 select-none"
            onPointerDown={() => {
              hold.current = window.setTimeout(() => setPaused(true), 300)
            }}
            onPointerUp={() => hold.current && clearTimeout(hold.current)}
          >
            <div
              key={recipe.id}
              className="absolute inset-0 flex items-center justify-center p-4 motion-safe:animate-in motion-safe:duration-500 motion-safe:fade-in-0"
            >
              {picture ? (
                <img
                  src={photoUrl(picture.ref, 'display')}
                  alt={recipe.card ? `The card for ${recipe.title}` : recipe.title}
                  className="h-auto max-h-full w-auto max-w-full rounded-sm object-contain shadow-[0_20px_60px_rgb(0_0_0/0.5)]"
                />
              ) : (
                <p className="max-w-md text-center text-3xl font-semibold text-balance">{recipe.title}</p>
              )}
            </div>
            <button
              type="button"
              aria-label="Previous card"
              disabled={at === 0}
              onClick={() => go(-1)}
              className="absolute inset-y-0 left-0 w-1/3 outline-none focus-visible:bg-viewer-foreground/5"
            />
            <button
              type="button"
              aria-label="Next card"
              disabled={at === items.length - 1}
              onClick={() => go(1)}
              className="absolute inset-y-0 right-0 w-2/3 outline-none focus-visible:bg-viewer-foreground/5"
            />
          </div>

          <div className="px-4 pt-2 pb-[calc(var(--safe-bottom)+0.75rem)]">
            <Link
              to={`/recipe/${recipe.id}`}
              viewTransition
              className={cn(
                'flex h-14 w-full items-center justify-center rounded-full bg-viewer-foreground text-base font-semibold text-viewer outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60 sm:mx-auto sm:max-w-sm',
              )}
            >
              Open the recipe
            </Link>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
